import { FileMetadata, Flow, FormResponse } from '@dynamic-flow/types';

// Vite environment variables
declare global {
  interface ImportMeta {
    readonly env: {
      readonly VITE_API_URL?: string;
    };
  }
}

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Types for preview app - focused on form rendering and submission
interface FlowApiResponse {
  success: boolean;
  data: Flow;
  message?: string;
}

// Union type for both response formats

// Generic API client for preview app
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      headers: {
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // If the error response has structured data, throw it as an object
        if (
          errorData.error &&
          (errorData.details || errorData.reason || errorData.metadata)
        ) {
          throw errorData; // Throw the structured error object
        } else {
          // Fallback to standard error format
          throw new Error(
            errorData.error || `HTTP ${response.status}: ${response.statusText}`
          );
        }
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Flow endpoints - only what's needed for preview
  async getDraftFlow(id: string, lang: string): Promise<FlowApiResponse> {
    return this.request<FlowApiResponse>(`/flows/${id}/draft?lang=${lang}`);
  }

  async getPublishedFlow(id: string, lang: string): Promise<FlowApiResponse> {
    return this.request<FlowApiResponse>(`/flows/${id}/published?lang=${lang}`);
  }

  // Response submission
  async submitResponse(
    response: FormResponse,
    files?: FileMetadata[]
  ): Promise<{ id: string; message: string; submittedAt: string }> {
    const formData = new FormData();

    // Append JSON data
    formData.append('data', JSON.stringify(response));

    // Append files with their question IDs
    files?.forEach((fileData) => {
      formData.append(fileData.questionId, fileData.file);
    });

    return this.request<{ id: string; message: string; submittedAt: string }>(
      '/responses',
      {
        method: 'POST',
        body: formData,
      }
    );
  }

  // Health check
  async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    version: string;
  }> {
    return this.request<{ status: string; timestamp: string; version: string }>(
      '/health',
      {
        method: 'GET',
      }
    );
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Convenience functions for flows
export const flowsApi = {
  getDraft: (id: string, lang: string) => apiClient.getDraftFlow(id, lang),
  getPublished: (id: string, lang: string) => apiClient.getPublishedFlow(id, lang),
};

// Convenience functions for responses
export const responsesApi = {
  submit: (response: FormResponse, files?: FileMetadata[]) =>
    apiClient.submitResponse(response, files),
};

// Convenience functions for responses
export const consentApi = {
  getPurposes: (response: FormResponse, files?: FileMetadata[]) =>
    apiClient.submitResponse(response, files),
};

// Convenience function for form submission
export const submitFormResponse = async (
  responseData: FormResponse,
  files?: FileMetadata[]
) => {
  return responsesApi.submit(responseData, files);
};

export default apiClient;
